import React from "react";
import {Controller, useFormContext} from "react-hook-form";
import {Box} from "@bounce/shared/ui";
import {PeakDay} from "../../../../apis/requests/payment-flows/types";
import ChipsSelectInput from "./ChipsSelectInput";
import {DAYS_OF_MONTH, HOURS, LAST_DAY_OF_MONTH} from "./constants";
import FieldLabel from "../../flow-form/FieldLabel";
import NumberField from "./NumberField";
import {IconTrendingUp} from "../../../../assets";
import {PolicyBlock} from "./PolicyBlock";
import {FormValues} from "./schema";
import {usePermittedHours} from "./usePermittedHours";

const PEAK_DAYS_HELP =
  "Days of the month when consumers are likely to have funds (paydays). Adds the peak hours on those dates.";
const PEAK_DAY_OPTIONS: readonly PeakDay[] = [...DAYS_OF_MONTH, LAST_DAY_OF_MONTH];

const PeakDaysBlock = ({variantIndex}: {variantIndex: number}) => {
  const {control} = useFormContext<FormValues>();
  const policyPath = `variants.${variantIndex}.policy` as const;
  const {isNotPermitted} = usePermittedHours(variantIndex);

  return (
    <PolicyBlock title="Peak days" icon={IconTrendingUp} help={PEAK_DAYS_HELP}>
      <Box sx={{display: "grid", gridTemplateColumns: "2fr 2fr 1fr", gap: 2}}>
        <Box>
          <FieldLabel>Day of month</FieldLabel>
          <Controller
            name={`${policyPath}.peak_day`}
            control={control}
            render={({field}) => (
              <ChipsSelectInput
                value={field.value}
                options={PEAK_DAY_OPTIONS}
                onChange={field.onChange}
                placeholder="+ day"
                ariaLabel="Peak days of month"
              />
            )}
          />
        </Box>
        <Box>
          <FieldLabel>Peak hours</FieldLabel>
          <Controller
            name={`${policyPath}.peak_day_hours`}
            control={control}
            render={({field}) => (
              <ChipsSelectInput
                value={field.value}
                options={HOURS}
                isInvalid={isNotPermitted}
                onChange={field.onChange}
                placeholder="+ hour"
                ariaLabel="Peak hours"
              />
            )}
          />
        </Box>
        <Box>
          <FieldLabel>Starts from day</FieldLabel>
          <Controller
            name={`${policyPath}.peak_min_day`}
            control={control}
            render={({field, fieldState}) => (
              <NumberField
                value={field.value}
                onChange={field.onChange}
                ariaLabel="Peak starts from day"
                error={!!fieldState.error}
              />
            )}
          />
        </Box>
      </Box>
    </PolicyBlock>
  );
};

export default PeakDaysBlock;
